import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DashboardOutlined as DashboardIcon,
  EmojiEventsOutlined as GamesIcon,
  PeopleOutline as CreatorsIcon,
  HandshakeOutlined as PartnersIcon,
  CardGiftcardOutlined as GiftCardIcon,
  NewspaperOutlined as NewsIcon,
  NotificationsNoneOutlined as NotificationIcon,
  ReceiptLongOutlined as TaxationIcon,
  SettingsOutlined as SettingsIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  colors,
  borderRadiusPx,
} from "../../../../theme";
import { Path } from "../../../../static/Constants";
import { CommonChip } from "../../ui";
import autoBetLogo from "../../../../static/Images/logo.png";

const SIDEBAR_WIDTH = 250;

export const defaultCreatorNavItems = [
  { label: "Dashboard", path: Path.org, icon: <DashboardIcon /> },
  {
    label: "Create Lottery",
    path: Path.orgcreatelottery,
    icon: <CreatorsIcon />,
  },
  { label: "My Lotteries", path: Path.orgmylottery, icon: <GamesIcon /> },
  // {
  //   label: "Financial Reports",
  //   path: Path.orgmylottery,
  //   icon: <ReportsIcon />,
  // },
  {
    label: "Gift Cards / BetCards",
    path: Path.orgcreatecard,
    icon: <GiftCardIcon />,
  },
  {
    label: "My Gift Cards",
    path: Path.mygiftcard,
    icon: <GiftCardIcon />,
  },
  {
    label: "Notifications",
    path: Path.orgNotifications,
    icon: <NotificationIcon />,
  },
  { label: "Profile & Settings", path: Path.profile, icon: <SettingsIcon /> },
  // { label: "Audit Logs", path: Path.orgmylottery, icon: <AuditIcon /> },
];

export const defaultAdminNavItems = [
  { label: "Dashboard", path: Path.admin, icon: <DashboardIcon /> },
  {
    label: "Creators List",
    path: Path.adminorglist,
    icon: <CreatorsIcon />,
  },
  { label: "Lotteries List", path: Path.adminlotterylist, icon: <GamesIcon /> },
  {
    label: "Partners List",
    path: Path.adminpartnerslist,
    icon: <PartnersIcon />,
  },
  {
    label: "Gift Cards List",
    path: Path.admingiftcard,
    icon: <GiftCardIcon />,
  },
  { label: "News Management", path: Path.adminNews, icon: <NewsIcon /> },
  {
    label: "Government Tax",
    path: Path.adminTaxation,
    icon: <TaxationIcon />,
  },
  {
    label: "Notifications",
    path: Path.adminNotifications,
    icon: <NotificationIcon />,
  },
  { label: "Profile & Settings", path: Path.profile, icon: <SettingsIcon /> },
];

/**
 * DashboardSidebar - Modern dark sidebar navigation for AutoBET Dashboards (Creator & Admin).
 */
const DashboardSidebar = ({
  open = true,
  onToggle,
  mobileOpen = false,
  onMobileToggle,
  currentQuarter = "",
  quarterRange = "",
  items,
  role,
  customNavItems = null,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const sb = colors.sidebar;

  const resolvedRole =
    role ||
    (location.pathname === Path.admin ||
    location.pathname.startsWith(`${Path.admin}/`) ||
    location.pathname.startsWith("/admin")
      ? "admin"
      : "creator");

  const navItems =
    customNavItems ||
    items ||
    (resolvedRole === "admin" ? defaultAdminNavItems : defaultCreatorNavItems);

  const isActive = (itemPath) => {
    if (!itemPath) return false;
    if (itemPath === Path.org || itemPath === Path.admin) {
      return location.pathname === itemPath;
    }
    if (itemPath.startsWith(Path.profile)) {
      return location.pathname === Path.profile;
    }
    if (location.pathname === itemPath) return true;
    if (location.pathname.startsWith(`${itemPath}/`)) return true;
    if (
      itemPath === Path.adminlotterylist &&
      (location.pathname.startsWith(Path.adminLotteryDetailsBase) ||
        location.pathname.startsWith(`${Path.adminLotteryDetailsBase}s`))
    ) {
      return true;
    }
    if (
      itemPath === Path.admingiftcard &&
      (location.pathname.startsWith(Path.adminGiftCardDetailsBase) ||
        location.pathname.startsWith(`${Path.adminGiftCardDetailsBase}s`))
    ) {
      return true;
    }
    if (
      itemPath === Path.orgmylottery &&
      (location.pathname.startsWith(Path.orgLotteryDetailsBase) ||
        location.pathname.startsWith(`${Path.orgLotteryDetailsBase}s`))
    ) {
      return true;
    }
    if (
      itemPath === Path.mygiftcard &&
      (location.pathname.startsWith(Path.orgGiftCardDetailsBase) ||
        location.pathname.startsWith(`${Path.orgGiftCardDetailsBase}s`))
    ) {
      return true;
    }
    return location.pathname.startsWith(itemPath);
  };

  const sidebarContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: sb.bg,
        color: sb.textActive,
        borderRight: `1px solid ${sb.border}`,
        "& a, & a:hover, & a:focus, & a:active, & a:visited": {
          textDecoration: "none !important",
        },
      }}
    >
      {/* Brand Header */}
      <Box
        sx={{
          p: "18px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          component={Link}
          to="/"
          sx={{
            textDecoration: "none !important",
            color: "inherit",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "none !important",
              color: "inherit",
            },
            "&:focus, &:active, &:visited": {
              textDecoration: "none !important",
              color: "inherit",
            },
            "& *": {
              textDecoration: "none !important",
            },
          }}
        >
          {/* Autobet Logo */}
          <Box
            component="img"
            src={autoBetLogo}
            alt="AutoBET Logo"
            sx={{
              width: 34,
              height: 34,
              borderRadius: borderRadiusPx.sm,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
              color: sb.textActive,
              textDecoration: "none !important",
            }}
          >
            Auto
            <Box
              component="span"
              sx={{ color: sb.textActive, textDecoration: "none !important" }}
            >
              BET
            </Box>
          </Typography>
        </Stack>

        {isMobile ? (
          <IconButton
            size="small"
            onClick={onMobileToggle}
            sx={{ color: sb.text }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{
              color: sb.text,
              "&:hover": {
                color: sb.textHover,
                backgroundColor: sb.itemHoverBg,
              },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Navigation Links */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          py: 1.5,
          px: 1.5,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.1)",
            borderRadius: borderRadiusPx.xs,
          },
        }}
      >
        <List disablePadding>
          {navItems.map((item, index) => {
            const active = isActive(item.path);

            return (
              <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={() => isMobile && onMobileToggle && onMobileToggle()}
                  sx={{
                    borderRadius: borderRadiusPx.lg,
                    py: 0.9,
                    px: 1.5,
                    backgroundColor: active ? sb.itemActiveBg : "transparent",
                    color: active ? sb.textActive : sb.text,
                    textDecoration: "none !important",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: active
                        ? sb.itemActiveHover
                        : sb.itemHoverBg,
                      color: sb.textHover,
                      textDecoration: "none !important",
                      "& .MuiListItemIcon-root": { color: sb.iconActive },
                      "& .MuiListItemText-primary": {
                        color: sb.textHover,
                        textDecoration: "none !important",
                      },
                    },
                    "&:focus, &:active, &:visited": {
                      textDecoration: "none !important",
                    },
                    "& *": {
                      textDecoration: "none !important",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: active ? sb.iconActive : sb.icon,
                      fontSize: "1.1rem",
                      "& svg": { fontSize: "1.2rem" },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "0.83rem",
                      fontWeight: active ? 600 : 500,
                      color: active ? sb.textActive : sb.text,
                      letterSpacing: "-0.01em",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textDecoration: "none !important",
                    }}
                  />

                  {item.badge && (
                    <CommonChip
                      label={item.badge}
                      variant="sidebarBadge"
                      sx={{
                        height: 18,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        borderRadius: borderRadiusPx.xs,
                        px: 0.5,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
      {/* Bottom Quarter Status Box */}
      {/* {currentQuarter && (
        <>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
          <Box sx={{ p: 2, backgroundColor: sb.quarterBoxBg }}>
            <Typography
              variant="caption"
              sx={{
                color: sb.quarterLabel,
                fontSize: "0.72rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                display: "block",
                mb: 0.25,
              }}
            >
              Current Quarter
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "0.88rem",
                color: sb.quarterTitle,
                mb: 0.25,
              }}
            >
              {currentQuarter}
            </Typography>
            {quarterRange && (
              <Typography
                variant="caption"
                sx={{ color: sb.quarterSub, fontSize: "0.74rem" }}
              >
                {quarterRange}
              </Typography>
            )}
          </Box>
        </>
      )} */}
    </Box>
  );

  return (
    <>
      {/* Desktop Collapsible / Persistent Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { md: open ? SIDEBAR_WIDTH : 0 },
          flexShrink: { md: 0 },
          display: { xs: "none", md: "block" },
          transition: "width 0.25s ease-in-out",
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: SIDEBAR_WIDTH,
            height: "100vh",
            zIndex: 1200,
            transform: {
              md: open ? "translateX(0)" : `translateX(-${SIDEBAR_WIDTH}px)`,
            },
            transition: "transform 0.25s ease-in-out",
          }}
        >
          {sidebarContent}
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={Boolean(mobileOpen)}
        onClose={onMobileToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
            backgroundColor: sb.bg,
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </>
  );
};

DashboardSidebar.propTypes = {
  open: PropTypes.bool,
  onToggle: PropTypes.func,
  mobileOpen: PropTypes.bool,
  onMobileToggle: PropTypes.func,
  currentQuarter: PropTypes.string,
  quarterRange: PropTypes.string,
  items: PropTypes.array,
  role: PropTypes.string,
  customNavItems: PropTypes.array,
};

export default DashboardSidebar;
export { SIDEBAR_WIDTH };
